import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGetMasterWithSubsQuery } from 'features/master/api/master.api';
import { MasterDetail, ProjectDetail } from 'features/master/api/types';
import { CircleArrowUp, FileDiff, ScanText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useGetGlobalPromptSectionsQuery, useGetProjectPromptSectionsQuery } from '../api/promptSections.api';
import { getAgentModelShortName } from '../lib/agentModels';
import { compilePrompt } from '../lib/promptCompiler';
import { PROMPT_LABEL_META, isPromptLabel } from '../lib/promptLabels';
import {
  PromptLabel,
  PromptType,
  PromptVersion,
  useChangePromptVersionLabelMutation,
  useGetAgentModelOptionsQuery,
  useListPromptVersionsQuery,
  useSetPromptVersionModelMutation,
} from '../services/projects.api';
import ChangeLabelDialog from './ChangeLabelDialog';
import ChangeModelPopover from './ChangeModelPopover';
import PromptDiffDialog from './PromptDiffDialog';
import PromptPreview from './PromptPreview';

const PAGE_SIZE = 10;

const DATE_FORMATTER = new Intl.DateTimeFormat('es-CL', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return DATE_FORMATTER.format(date);
};

const findSubProject = (master: MasterDetail | undefined, subProjectId: number): ProjectDetail | null => {
  if (!master?.subProjects?.length) return null;
  return master.subProjects.find((sub) => sub.id === subProjectId) ?? null;
};

const VersionLabelBadge = ({ label }: { label: string | null }): JSX.Element | null => {
  if (!isPromptLabel(label)) return null;
  const meta = PROMPT_LABEL_META[label];
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}>{meta.text}</span>;
};

interface VersionCardProps {
  version: PromptVersion;
  modelName: string;
  selected: boolean;
  canCompare: boolean;
  canChangeLabel: boolean;
  onSelect: () => void;
  onCompare: () => void;
  onChangeLabel: () => void;
}

const VersionCard = ({
  version,
  modelName,
  selected,
  canCompare,
  canChangeLabel,
  onSelect,
  onCompare,
  onChangeLabel,
}: VersionCardProps): JSX.Element => (
  <div
    className={`group relative flex w-full flex-col gap-2 border-b border-border-subtle px-4 py-3.5 transition-colors ${
      selected ? 'bg-gray-100' : 'bg-bg-surface hover:bg-gray-50'
    }`}
  >
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Ver versión ${version.version}`}
      className="absolute inset-0 z-0 cursor-pointer"
    />
    <div className="pointer-events-none relative z-10 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold text-text-default"># {version.version}</span>
        <VersionLabelBadge label={version.label} />
        <button
          type="button"
          onClick={onChangeLabel}
          title="Cambiar etiqueta"
          aria-label="Cambiar etiqueta"
          disabled={!canChangeLabel}
          className={`pointer-events-auto rounded-md border border-border-subtle bg-white p-1 text-text-muted opacity-0 shadow-sm transition-opacity hover:text-text-default group-hover:opacity-100 ${
            !canChangeLabel ? 'invisible' : ''
          }`}
        >
          <CircleArrowUp className="size-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-[11px] text-text-muted">
          <span className="truncate">{modelName}</span>
          <span>
            {formatDate(version.createdAt)}{' '}
            <span className="text-text-subtle">by {version.createdByName ?? 'API'}</span>
          </span>
        </span>
        <button
          type="button"
          onClick={onCompare}
          title="Comparar con la versión seleccionada"
          aria-label="Comparar con la versión seleccionada"
          disabled={!canCompare}
          className={`pointer-events-auto rounded-md border border-border-subtle bg-white p-1 text-text-muted opacity-0 shadow-sm transition-opacity hover:text-text-default group-hover:opacity-100 ${
            !canCompare ? 'invisible' : ''
          }`}
        >
          <FileDiff className="size-3.5" />
        </button>
      </div>
    </div>
  </div>
);

const PromptView = (): JSX.Element => {
  const { masterProjectId, subProjectId } = useParams();
  const navigate = useNavigate();
  const masterId = masterProjectId !== undefined ? Number(masterProjectId) : null;
  const subId = subProjectId !== undefined ? Number(subProjectId) : null;
  const isValidMasterId = masterId !== null && Number.isFinite(masterId);
  const isValidSubId = subId === null || Number.isFinite(subId);
  const isMasterRoute = subId === null;

  const {
    data: master,
    isLoading: isMasterLoading,
    isError: isMasterError,
  } = useGetMasterWithSubsQuery(masterId ?? 0, { skip: !isValidMasterId });

  const subProject = !isMasterRoute && master && subId !== null ? findSubProject(master, subId) : null;
  const project: ProjectDetail | MasterDetail | null | undefined = isMasterRoute ? master : subProject;

  const promptType: PromptType = isMasterRoute ? 'common' : 'subproject';
  const queryProjectId = isMasterRoute ? masterId : subId;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [compareVersion, setCompareVersion] = useState<PromptVersion | null>(null);
  const [labelVersion, setLabelVersion] = useState<PromptVersion | null>(null);
  const [setPromptVersionModel, { isLoading: isChangingModel }] = useSetPromptVersionModelMutation();
  const [previewActive, setPreviewActive] = useState(false);

  const [changePromptVersionLabel, { isLoading: isChangingLabel }] = useChangePromptVersionLabelMutation();
  const { data: modelOptions } = useGetAgentModelOptionsQuery(queryProjectId ?? 0, {
    skip: queryProjectId === null,
  });

  useEffect(() => {
    setCurrentPage(1);
    setSelectedVersionId(null);
    setPreviewActive(false);
  }, [queryProjectId, promptType]);

  const { data: globalSectionsData, isLoading: globalSectionsLoading } = useGetGlobalPromptSectionsQuery(
    { offset: 0, limit: 500 },
    { skip: !previewActive }
  );
  const { data: projectSectionsData, isLoading: projectSectionsLoading } = useGetProjectPromptSectionsQuery(
    { projectId: queryProjectId ?? 0, offset: 0, limit: 500 },
    { skip: !previewActive || queryProjectId === null }
  );
  const sectionsLoading = previewActive && (globalSectionsLoading || projectSectionsLoading);

  const {
    data: page,
    isLoading: isVersionsLoading,
    isError: isVersionsError,
  } = useListPromptVersionsQuery(
    {
      projectId: queryProjectId ?? 0,
      promptType,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    },
    {
      skip: !isValidMasterId || !isValidSubId || queryProjectId === null || !project,
      refetchOnMountOrArgChange: true,
    }
  );

  const versions = page?.items;
  const visibleVersions = versions ?? [];
  const totalPages = Math.ceil((page?.total ?? 0) / PAGE_SIZE);

  let selectedVersion: PromptVersion | null = null;
  if (visibleVersions.length) {
    if (selectedVersionId !== null) {
      selectedVersion = visibleVersions.find((v) => v.id === selectedVersionId) ?? null;
    }
    selectedVersion ??= visibleVersions[0];
  }

  const productionVersion = visibleVersions.find((v) => v.label === 'production') ?? null;

  // Same args as page 1, so RTK Query shares the request. The first page has the newest versions, so the latest one.
  const { data: firstPage } = useListPromptVersionsQuery(
    { projectId: queryProjectId ?? 0, promptType, limit: PAGE_SIZE, offset: 0 },
    { skip: !isValidMasterId || !isValidSubId || queryProjectId === null || !project }
  );
  const latestVersion = firstPage?.items.find((v) => v.label === 'latest') ?? null;
  // A master model covers every subproject, so applying it moves all of them.
  const movesSubprojects = isMasterRoute && !!master?.subProjects?.length;

  const compiledSegments = useMemo(() => {
    if (!previewActive || !selectedVersion || sectionsLoading) return null;
    return compilePrompt({
      rawContent: selectedVersion.content,
      globalSections: globalSectionsData?.globalPromptSections ?? [],
      projectSections: projectSectionsData?.promptSections ?? [],
      masterProject: master,
      subProject: subProject ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    previewActive,
    selectedVersion?.id,
    sectionsLoading,
    globalSectionsData,
    projectSectionsData,
    master,
    subProject,
  ]);

  if (!isValidMasterId || !isValidSubId) {
    return (
      <section className="max-w-4xl">
        <p className="text-sm text-text-muted">No se ha seleccionado un proyecto válido.</p>
      </section>
    );
  }

  if (isMasterLoading) {
    return (
      <section className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <div className="flex gap-4">
          <Skeleton className="h-[600px] w-[260px]" />
          <Skeleton className="h-[600px] flex-1" />
        </div>
      </section>
    );
  }

  if (isMasterError || !project || masterId === null) {
    return (
      <section className="max-w-4xl">
        <p className="text-sm text-destructive">No se pudo cargar el proyecto.</p>
      </section>
    );
  }

  const eyebrow = isMasterRoute ? 'PROMPT BASE' : 'PROMPT SUBPROYECTO';
  const title = isMasterRoute ? 'Prompt Común' : project.name;

  return (
    <section className="flex h-full min-h-[640px] flex-col overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
      <header className="flex items-center justify-between gap-4 border-b border-border-subtle px-6 py-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-widest text-text-subtle">{eyebrow}</span>
          <div className="flex items-center gap-2">
            <h1 className="m-0 text-xl font-semibold text-text-default">{title}</h1>
            {productionVersion && (
              <Badge className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100">
                Production
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {modelOptions && queryProjectId !== null && (
            <ChangeModelPopover
              modelOptions={modelOptions}
              latestVersion={latestVersion}
              isSaving={isChangingModel}
              onSelect={async (model) => {
                if (!latestVersion) return;
                await setPromptVersionModel({
                  projectId: queryProjectId,
                  promptVersionId: latestVersion.id,
                  model,
                }).unwrap();
              }}
            />
          )}
          <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/project/${masterId}/demo-chat`)}>
            Demo Chat
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-gray-900 text-white hover:bg-gray-800"
            onClick={() =>
              navigate(
                isMasterRoute
                  ? `/project/${masterId}/prompt/new-version`
                  : `/project/${masterId}/prompt/${subId}/new-version`,
                { state: { baseVersion: selectedVersion } }
              )
            }
          >
            + Nueva versión
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[260px] flex-col border-r border-border-subtle">
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <span className="text-[13px] font-semibold text-text-default">Versiones</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-text-muted">
              {page?.total ?? visibleVersions.length}
            </span>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            {isVersionsLoading && (
              <>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </>
            )}
            {!isVersionsLoading && isVersionsError && (
              <p className="px-2 text-xs text-destructive">No se pudieron cargar las versiones.</p>
            )}
            {!isVersionsLoading && !isVersionsError && visibleVersions.length === 0 && (
              <p className="px-2 text-xs text-text-muted">Aún no hay versiones.</p>
            )}
            {visibleVersions.map((version) => (
              <VersionCard
                key={version.id}
                version={version}
                modelName={getAgentModelShortName(version.agentAIModel, modelOptions?.options)}
                selected={selectedVersion?.id === version.id}
                canCompare={!!selectedVersion && selectedVersion.id !== version.id}
                canChangeLabel={version.id !== 0}
                onSelect={() => setSelectedVersionId(version.id)}
                onCompare={() => setCompareVersion(version)}
                onChangeLabel={() => setLabelVersion(version)}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <Pagination className="border-t border-border-subtle py-2">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    text=""
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(Math.max(1, currentPage - 1));
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-2 text-xs text-text-muted">
                    {currentPage} / {totalPages}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    text=""
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(Math.min(totalPages, currentPage + 1));
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </aside>

        <main className="flex min-w-0 flex-1 flex-col">
          {selectedVersion ? (
            <>
              <div className="flex items-center gap-2 border-b border-border-subtle bg-bg-muted/40 px-6 py-2.5">
                <span className="text-[13px] font-semibold text-text-default">#{selectedVersion.version}</span>
                <VersionLabelBadge label={selectedVersion.label} />
                <span className="text-text-subtle">·</span>
                <span className="whitespace-nowrap text-xs text-text-muted">
                  {formatDate(selectedVersion.createdAt)}
                </span>
                <span className="text-text-subtle">·</span>
                <span className="truncate text-xs text-text-muted">{selectedVersion.createdByName ?? 'API'}</span>
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewActive((v) => !v)}
                    title={previewActive ? 'Ver texto sin compilar' : 'Previsualizar secciones resueltas'}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                      previewActive
                        ? 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100'
                        : 'border-border-subtle bg-white text-text-muted hover:bg-gray-50 hover:text-text-default'
                    )}
                  >
                    <ScanText className="size-3.5" />
                    {previewActive ? 'Compilado' : 'Previsualizar'}
                  </button>
                  {selectedVersion.changeReason && (
                    <Popover>
                      <PopoverTrigger className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
                        Ver comentario
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-80">
                        <PopoverHeader>
                          <PopoverTitle className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                            Razón del cambio
                          </PopoverTitle>
                        </PopoverHeader>
                        <p className="whitespace-pre-wrap text-xs leading-relaxed text-text-default">
                          {selectedVersion.changeReason}
                        </p>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              {previewActive && sectionsLoading && (
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              )}
              {previewActive && !sectionsLoading && compiledSegments && <PromptPreview segments={compiledSegments} />}
              {!previewActive && (
                <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap bg-gray-50 p-4 font-mono text-xs leading-relaxed text-text-default">
                  {selectedVersion.content}
                </pre>
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6">
              <p className="text-sm text-text-muted">
                {isVersionsLoading ? 'Cargando versiones…' : 'Selecciona una versión para ver su contenido.'}
              </p>
            </div>
          )}
        </main>
      </div>

      {compareVersion && (
        <PromptDiffDialog
          open
          onOpenChange={(open) => {
            if (!open) setCompareVersion(null);
          }}
          title="Comparar versiones"
          subtitle={selectedVersion ? `#${compareVersion.version} → #${selectedVersion.version}` : undefined}
          oldContent={compareVersion.content}
          newContent={selectedVersion?.content ?? ''}
        />
      )}

      {labelVersion && (
        <ChangeLabelDialog
          open
          onOpenChange={(open) => {
            if (!open) setLabelVersion(null);
          }}
          versionLabel={`#${labelVersion.version}`}
          currentLabel={labelVersion.label}
          isSaving={isChangingLabel}
          versionModel={labelVersion.agentAIModel}
          modelOptions={modelOptions}
          movesSubprojects={movesSubprojects}
          onSave={async (label: PromptLabel, applyModel: boolean) => {
            if (queryProjectId === null) return;
            // Let the error propagate so ChangeLabelDialog can display it
            // (e.g. the backend rejecting a direct demotion of production).
            await changePromptVersionLabel({
              projectId: queryProjectId,
              promptVersionId: labelVersion.id,
              label,
              applyModel,
            }).unwrap();
            setLabelVersion(null);
          }}
        />
      )}
    </section>
  );
};

export default PromptView;
